import { AgencyType, IAgency, IUser, Role } from '@/models';

export const isUserRoleAgencyEqualsToAgencyId = (
  userRoleAgency: string | IAgency | undefined,
  agencyId: string,
) => {
  return (
    (typeof userRoleAgency === 'string' && userRoleAgency === agencyId) ||
    (typeof userRoleAgency !== 'string' && userRoleAgency?._id === agencyId)
  );
};

export const isAccountAdmin = (user: IUser) => {
  return isTargetRole(user, Role.ACCOUNT_ADMIN);
};

export const isDispatcherRole = (user: IUser) => {
  return isTargetRole(user, Role.DISPATCHER);
};

export const isAgencyAdmin = (user: IUser, agencyId?: string) => {
  return isTargetAgencyRole(user, Role.AGENCY_ADMIN, agencyId);
};

export const isAdmin = (user: IUser, agencyId: string) => {
  return isAccountAdmin(user) || isAgencyAdmin(user, agencyId);
};

export const isTargetRole = (user: IUser, targetRole: Role) => {
  return !!user.roles?.find(({ role }) => role === targetRole);
};
export const isAnyTargetRole = (user: IUser, targetRoles: Role[]) => {
  return user.roles?.some(({ role }) => targetRoles.includes(role)) ?? false;
};

export const isTargetAgencyRole = (
  user: IUser,
  targetRole: Role,
  agencyId?: string,
) => {
  return !!user.roles?.find(
    ({ role, agency }) =>
      role === targetRole &&
      agencyId &&
      isUserRoleAgencyEqualsToAgencyId(agency, agencyId),
  );
};

// Check if user has another role except given roles
export const hasOtherRoleExcept = (user: IUser, roles: Role[]) => {
  return !!user.roles.find((userRole) => !roles.includes(userRole.role));
};

// Check if user has given role of given agency type
export const isAgencyTypeRole = (
  user: IUser,
  expectedRole: Role,
  expectedAgencyType: AgencyType,
) => {
  return !!user.roles.find((userRole) => {
    const agency = userRole.agency as IAgency | undefined;
    return (
      agency?.type === expectedAgencyType && expectedRole === userRole.role
    );
  });
};

//
export const isAgencyEnabledJMS = (user: IUser) => {
  return !!user.roles.find((userRole) => {
    const agency = userRole.agency as IAgency | undefined;
    return agency?.isEnabledJMS;
  });
};

export const hasAnyOfTargetRoles = (
  user: IUser,
  roles: Role[],
  agencyId?: string,
) => {
  let userRoles = user.roles;
  if (agencyId) {
    userRoles = userRoles.filter(({ agency }) =>
      isUserRoleAgencyEqualsToAgencyId(agency, agencyId),
    );
  }
  return userRoles.some(({ role }) => roles.indexOf(role) > -1);
};

// Filter agencies based on user roles
export const getUserPermittedAgencies = (
  user: IUser,
  agencies: IAgency[],
): IAgency[] => {
  // Account Admins can see all agencies regardless of other roles
  if (isAccountAdmin(user)) {
    return agencies;
  }

  // If user has AGENCY_ADMIN role (but not ACCOUNT_ADMIN), only show agencies they are assigned to,
  // even if they also have other roles like DISPATCHER
  if (user.roles.some((role) => role.role === Role.AGENCY_ADMIN)) {
    const agencyAdminRoles = user.roles.filter(
      (role) => role.role === Role.AGENCY_ADMIN && role.agency,
    );

    const permittedAgencyIds = agencyAdminRoles
      .map((role) => {
        if (typeof role.agency === 'string') {
          return role.agency;
        }
        return (role.agency as IAgency)?._id;
      })
      .filter(Boolean);

    return agencies.filter((agency) => permittedAgencyIds.includes(agency._id));
  }

  // Dispatchers can see all agencies if they don't have AGENCY_ADMIN role
  if (isDispatcherRole(user)) {
    return agencies;
  }

  return agencies;
};

// Roles that can perform at least one CFS modify action (union of the CFS write-endpoint role
// lists). Used to detect a "pure" CFS (View Only) user so the UI hides every CFS modify control
// for them while leaving every other role's experience unchanged.
const CFS_EDIT_CAPABLE_ROLES: Role[] = [
  Role.ACCOUNT_ADMIN,
  Role.DISPATCHER,
  Role.FIRST_RESPONDER,
  Role.AGENCY_ADMIN,
  Role.RMS_CASES_FULL_ACCESS,
  Role.KASE_APPROVER,
  Role.EVIDENCE_COLLECTOR,
  Role.EVIDENCE_TRANSPORTER,
  Role.EVIDENCE_CUSTODIAN,
];

// True when the user holds the CFS (View Only) role and no role that grants any CFS edit ability.
// "Highest access wins": a user who also holds an edit role (e.g. Dispatcher) is NOT view-only and
// keeps full access. All CFS modify controls should be hidden when this returns true.
export const isCFSViewOnly = (user?: IUser | null): boolean =>
  !!user &&
  isTargetRole(user, Role.CFS_VIEW_ONLY) &&
  !hasAnyOfTargetRoles(user, CFS_EDIT_CAPABLE_ROLES);

// Convenience inverse for gating: render CFS modify controls only when this is true.
export const canModifyCFS = (user?: IUser | null): boolean =>
  !isCFSViewOnly(user);
